import { HttpErrorResponse } from '@angular/common/http';
import type { FormGroup } from '@angular/forms';
import { catchError, map, throwError, type OperatorFunction } from 'rxjs';

/** Error individual dentro del envelope. `code` es snake_case y estable (es lo que se debe interpretar). */
export interface ApiError {
  code: string;
  message: string;
  field?: string | null;
  details?: Record<string, unknown> | null;
}

/** Envelope estándar de la plataforma (servicios .NET migrados). */
export interface ApiResponse<T> {
  success: boolean;
  object: T | null;
  errors: ApiError[];
  request_id?: string | null;
  meta?: Record<string, unknown>;
}

/** Envelope legacy de los servicios AÚN NO migrados. Se soporta para no romper nada durante la migración. */
export interface LegacyResponse<T> {
  errored: boolean;
  errorMessage?: string | null;
  errors?: string[] | null;
  object?: T | null;
}

/** Error normalizado: da igual si vino del envelope nuevo, del legacy o de un fallo HTTP pelado. */
export class ApiRequestError extends Error {
  readonly errors: ApiError[];
  readonly requestId: string | null;
  readonly status: number | null;

  constructor(errors: ApiError[], options: { requestId?: string | null; status?: number | null } = {}) {
    super(errors[0]?.message ?? 'Error inesperado. Contacte al administrador.');
    this.name = 'ApiRequestError';
    this.errors = errors;
    this.requestId = options.requestId ?? null;
    this.status = options.status ?? null;
  }

  /** Errores atribuibles a un campo: `{ rfc: 'El RFC es obligatorio.' }`. */
  get fieldErrors(): Record<string, string> {
    const result: Record<string, string> = {};
    for (const error of this.errors) {
      if (error.field && !(error.field in result)) result[error.field] = error.message;
    }
    return result;
  }

  /** `true` si algún error trae ese código (ej. `'not_found'`, `'conflict'`). */
  has(code: string): boolean {
    return this.errors.some((error) => error.code === code);
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

export function isApiResponse<T>(body: unknown): body is ApiResponse<T> {
  return isRecord(body) && typeof body['success'] === 'boolean' && Array.isArray(body['errors']);
}

export function isLegacyResponse<T>(body: unknown): body is LegacyResponse<T> {
  return isRecord(body) && typeof body['errored'] === 'boolean';
}

function legacyErrors<T>(body: LegacyResponse<T>): ApiError[] {
  const messages =
    body.errors?.length ? body.errors : [body.errorMessage ?? 'Error inesperado. Contacte al administrador.'];
  return messages.map((message) => ({ code: 'legacy_error', message }));
}

function httpFallback(status: number): ApiError {
  switch (status) {
    case 0:   return { code: 'network_error', message: 'Sin conexión con el servidor.' };
    case 400: return { code: 'validation_error', message: 'La petición no es válida.' };
    case 401: return { code: 'unauthorized', message: 'Tu sesión no existe o expiró.' };
    case 403: return { code: 'forbidden', message: 'No tienes permisos para esta operación.' };
    case 404: return { code: 'not_found', message: 'El recurso no existe.' };
    case 409: return { code: 'conflict', message: 'La operación entra en conflicto con el estado actual.' };
    case 422: return { code: 'business_rule_error', message: 'La operación no cumple una regla de negocio.' };
    default:  return status >= 500
      ? { code: 'infrastructure_error', message: 'Error en el sistema. Contacte al administrador.' }
      : { code: 'unexpected_error', message: 'Error inesperado. Contacte al administrador.' };
  }
}

/**
 * Desenvuelve el body de una respuesta exitosa:
 * - `ApiResponse<T>` (migrado) → devuelve `object`; si `success=false`, lanza `ApiRequestError`.
 * - `ResponseDto` legacy      → devuelve `object`; si `errored`, lanza `ApiRequestError`.
 * - Cualquier otra cosa (respuesta cruda, `Blob`, `string`) → la devuelve TAL CUAL.
 */
export function unwrapBody<T>(body: unknown): T {
  if (isApiResponse<T>(body)) {
    if (!body.success) throw new ApiRequestError(body.errors, { requestId: body.request_id });
    return body.object as T;
  }
  if (isLegacyResponse<T>(body)) {
    if (body.errored) throw new ApiRequestError(legacyErrors(body));
    return (body.object ?? null) as T;
  }
  return body as T;
}

/** Normaliza cualquier fallo (HttpErrorResponse con envelope, legacy, o error suelto) a `ApiRequestError`. */
export function toApiRequestError(cause: unknown): ApiRequestError {
  if (cause instanceof ApiRequestError) return cause;

  if (cause instanceof HttpErrorResponse) {
    const body: unknown = cause.error;

    if (isApiResponse<unknown>(body) && body.errors.length > 0) {
      return new ApiRequestError(body.errors, { requestId: body.request_id, status: cause.status });
    }
    if (isLegacyResponse<unknown>(body)) {
      return new ApiRequestError(legacyErrors(body), { status: cause.status });
    }
    return new ApiRequestError([httpFallback(cause.status)], { status: cause.status });
  }

  return new ApiRequestError([
    {
      code: 'unexpected_error',
      message: cause instanceof Error ? cause.message : 'Error inesperado. Contacte al administrador.',
    },
  ]);
}

/**
 * Operador opt-in: desenvuelve el envelope y normaliza los errores.
 * Úsalo en las llamadas a servicios YA migrados; los no migrados también funcionan (legacy y crudo).
 */
export function unwrapApiResponse<T>(): OperatorFunction<unknown, T> {
  return (source) =>
    source.pipe(
      map((body) => unwrapBody<T>(body)),
      catchError((cause: unknown) => throwError(() => toApiRequestError(cause))),
    );
}

/**
 * Pinta en un Reactive Form los errores que traen `field` (quedan como error `api` en el control)
 * y DEVUELVE los que no corresponden a ningún control, para mostrarlos como mensaje general.
 */
export function applyFieldErrors(error: ApiRequestError, form: FormGroup): ApiError[] {
  const unassigned: ApiError[] = [];

  for (const apiError of error.errors) {
    const control = apiError.field ? form.get(apiError.field) : null;
    if (control) {
      control.setErrors({ ...(control.errors ?? {}), api: apiError.message });
      control.markAsTouched();
    } else {
      unassigned.push(apiError);
    }
  }

  return unassigned;
}
